import React from "react";
import { X, Upload, Eye, BarChart3 } from "lucide-react";
import PreviewTab from "./PreviewTab";
import ResultsTab from "./ResultsTab";

const ImportModal = ({
  theme,
  language,
  isRTL,
  showImportModal,
  importPreview,
  importResult,
  importActiveTab,
  setImportActiveTab,
  isImporting,
  duplicateSelections,
  isResolvingDuplicates,
  onEditCell,
  onDeleteRow,
  onClose,
  onDuplicateSelect,
  onUpdateDuplicate,
  onResolveBulk,
  onProcessImport,
}) => {
  if (!showImportModal) return null;

  const isAr = language === "ar";

  const tabBtn = (key, icon, label) => {
    const active = importActiveTab === key;
    return (
      <button
        onClick={() => setImportActiveTab(key)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "8px 16px",
          borderRadius: "8px",
          border: "none",
          background: active
            ? theme === "dark"
              ? "rgba(59,130,246,0.25)"
              : "rgba(59,130,246,0.12)"
            : "transparent",
          color: active
            ? theme === "dark"
              ? "#93c5fd"
              : "#2563eb"
            : theme === "dark"
              ? "rgba(255,255,255,0.5)"
              : "#9ca3af",
          fontWeight: active ? "700" : "500",
          fontSize: "13px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          borderBottom: active
            ? `2px solid ${theme === "dark" ? "#93c5fd" : "#2563eb"}`
            : "2px solid transparent",
        }}
      >
        {icon}
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 3000,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        direction: isRTL ? "rtl" : "ltr",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: theme === "dark" ? "#1e293b" : "#fff",
          borderRadius: "20px",
          width: "min(94vw, 1100px)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            theme === "dark"
              ? "0 24px 64px rgba(0,0,0,0.7)"
              : "0 24px 64px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom:
              theme === "dark"
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(0,0,0,0.08)",
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: "700",
              color: theme === "dark" ? "#f1f5f9" : "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Upload size={20} />
            {isAr ? "استيراد سجلات الحضور" : "Import Attendance Records"}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close import modal"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.06)",
              background: theme === "dark" ? "rgba(255,255,255,0.03)" : "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 160ms ease",
              color: theme === "dark" ? "#e6eef8" : "#374151",
              boxShadow: theme === "dark" ? "0 6px 18px rgba(2,6,23,0.6)" : "0 6px 18px rgba(2,6,23,0.06)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = theme === "dark" ? "0 8px 22px rgba(2,6,23,0.8)" : "0 8px 22px rgba(2,6,23,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = theme === "dark" ? "0 6px 18px rgba(2,6,23,0.6)" : "0 6px 18px rgba(2,6,23,0.06)";
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            padding: "12px 24px 0",
            borderBottom:
              theme === "dark"
                ? "1px solid rgba(255,255,255,0.07)"
                : "1px solid rgba(0,0,0,0.06)",
            flexShrink: 0,
          }}
        >
          {tabBtn("preview", <Eye size={14} />, isAr ? "معاينة" : "Preview")}
          {importResult &&
            tabBtn(
              "results",
              <BarChart3 size={14} />,
              isAr ? "النتائج" : "Results"
            )}
        </div>

        {/* Body */}
        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            flex: 1,
            scrollbarWidth: "thin",
          }}
        >
          {importActiveTab === "preview" && (
            <PreviewTab
              theme={theme}
              language={language}
              rows={importPreview}
              onEditCell={onEditCell}
              onDeleteRow={onDeleteRow}
            />
          )}
          {importActiveTab === "results" && (
            <ResultsTab
              theme={theme}
              language={language}
              importResult={importResult}
              duplicateSelections={duplicateSelections}
              onDuplicateSelect={onDuplicateSelect}
              onUpdateDuplicate={onUpdateDuplicate}
              onResolveBulk={onResolveBulk}
              isResolvingDuplicates={isResolvingDuplicates}
            />
          )}
        </div>

        {/* Footer */}
        {importActiveTab === "preview" && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "16px 24px",
              borderTop:
                theme === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,0,0,0.07)",
              flexShrink: 0,
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px",
                borderRadius: "10px",
                border:
                  theme === "dark"
                    ? "1px solid rgba(255,255,255,0.15)"
                    : "1px solid rgba(0,0,0,0.12)",
                background: "transparent",
                color: theme === "dark" ? "#f1f5f9" : "#374151",
                fontWeight: "600",
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              {isAr ? "إلغاء" : "Cancel"}
            </button>
            <button
              onClick={onProcessImport}
              disabled={isImporting || importPreview.length === 0}
              style={{
                padding: "10px 24px",
                borderRadius: "10px",
                border: "none",
                background:
                  isImporting || importPreview.length === 0
                    ? theme === "dark"
                      ? "rgba(59,130,246,0.3)"
                      : "rgba(147,197,253,0.6)"
                    : "linear-gradient(135deg, #3b82f6, #2563eb)",
                color: "#fff",
                fontWeight: "700",
                fontSize: "13px",
                cursor:
                  isImporting || importPreview.length === 0
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: isImporting || importPreview.length === 0 ? 0.7 : 1,
              }}
            >
              <Upload size={15} />
              {isImporting
                ? isAr
                  ? "جارٍ الاستيراد..."
                  : "Importing..."
                : isAr
                  ? "استيراد"
                  : "Import"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportModal;